import React, { Fragment, useMemo, useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { useHistory } from 'react-router-dom';
import { Typography, Tooltip, Button, useMediaQuery } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import OutlinedFlagRoundedIcon from '@mui/icons-material/OutlinedFlagRounded';
import { ReactComponent as Preloader } from '#static/media/preloader.svg';
import { createTitle } from '#component/project/form/alerts/alert-item/helper';
import HighlightWord from '#component/general/highlight-word/highlight-word';
import { getHighlightedSearchResult } from '#component/general/highlight-word/helper';
import {
  CardWrapper,
  StyledCard,
  StyledCardHeader,
  SubheaderContainer,
  WarningContainer,
  StyledWarningIcon,
  ActionsButton,
  FlexCenterTypography,
  StyledCardContent,
  GroupLabel,
  DeviceInfoLink,
  NoMeasurementsContainer,
  NoDataWrapper,
  NoDataTitle,
  NoDataContent,
  ColoredSpan,
  StyledTooltip
} from './styles';
import DeviceInfo from './info';
import { detectExcursion } from '#helper';

const MAX_APPLIANCE_DEVICES = 7;
const FOUR_HOURS = 1000 * 60 * 60 * 4;
const STOPPED_STATUS = 300;

function getAlertTooltip(d, group, appliance, account) {
  const alert = appliance.alerts?.find(a => a.id === d.projectAlertId);
  return group.projectAlertId ?
    (
      d.projectAlertId ?
        createTitle(alert, 'metric', account.measurementSystem) :
        'Device does not have an alert'
    ) :
    `${group.label ? `${group.label} group` : 'Device'}  does not have an alert`;
}

const ApplianceCard = props => {
  const {
    status,
    account,
    appliance,
    applianceDevices,
    onClick
  } = props;
  const { userType } = account;
  const history = useHistory();
  const applianceFromStore =
    useSelector(state => state.appliances.appliances.find(a => a.id === appliance.id));
  const isApplianceStopped = (applianceData, applianceDataromStore) =>
    applianceData?.status === STOPPED_STATUS || applianceDataromStore?.status === STOPPED_STATUS;
  const listFilter = useSelector(s => s.listFilter, shallowEqual);
  const highlightedSearchResult = getHighlightedSearchResult(listFilter);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const deviceGroups = useMemo(
    () =>
      applianceDevices.filter(el => el.applianceId === appliance.id),
    [applianceDevices, appliance]
  );

  const devices = useMemo(
    () =>
      deviceGroups.reduce((acc, rec) => acc.concat(rec.devices), []),
    [deviceGroups]
  );

  const timeoutAlert = useMemo(
    () =>
      (devices.find(d => !d.lastMetric?.timestamp ||
      new Date().getTime() - new Date(d.lastMetric?.timestamp).getTime() > FOUR_HOURS)),
    [devices]
  );

  const devicesWithExcursions = useMemo(
    () =>
      devices.filter(d =>
        // a device group has an alert
        d.groupAlertId &&
        // an alert for a device is switched ON
        d.projectAlertId &&
        appliance?.alerts.find(a => {
          // eslint-disable-next-line no-param-reassign
          d.excursions = a.id === d.projectAlertId ?
            detectExcursion(a, d.lastMetric) :
            [];
          return d.excursions.length;
        })),
    [devices, appliance]
  );

  const handleClick = useCallback(e => {
    if (status === 'succeeded') {
      if ((e.ctrlKey && e.type === 'click')) {
        window.open(`/warehouse/appliance-overview/${appliance?.id}`, '_blank');
      } else {
        history.push(`/warehouse/appliance-overview/${appliance?.id}`);
      }
    }
  }, [appliance, history, status]);

  const warningMessages = [];
  if (timeoutAlert) warningMessages.push('A device has more than 4 hours time delay.');
  if (devicesWithExcursions.length) warningMessages.push('Detected a measurement excursion.');

  const applianceLabel = appliance.label?.length > 60 ? `${appliance.label.substring(0, 60)}...` : appliance.label;
  const warehouseLabel = appliance.warehouse.label?.length > 60 ? `${appliance.warehouse.label.substring(0, 60)}...` : appliance.warehouse.label;

  let counter = 0;
  return (
    <CardWrapper>
      <StyledCard key={appliance.id} variant="outlined">
        <StyledCardHeader
          onClick={handleClick}
          isClickable={status === 'succeeded'}
          subheader={
            <SubheaderContainer>
              <div>
                <FlexCenterTypography variant="h5" color="textPrimary" component="h2">
                  <WarningContainer>
                    {isApplianceStopped(appliance, applianceFromStore) &&
                      <Tooltip title="Stopped">
                        <OutlinedFlagRoundedIcon />
                      </Tooltip>}
                  </WarningContainer>

                  <WarningContainer>
                    {warningMessages.length ?
                      <Tooltip title={warningMessages.map(m => (<div>{m}</div>))}>
                        <StyledWarningIcon />
                      </Tooltip> : null}
                  </WarningContainer>
                  {highlightedSearchResult ? (
                    <span>
                      <HighlightWord
                        textToHighlight={applianceLabel}
                        searchWord={highlightedSearchResult}
                      />
                    </span>) : applianceLabel}
                </FlexCenterTypography>
                <Typography variant="body2" color="textSecondary">
                  {warehouseLabel}
                </Typography>
              </div>
              {userType < 20 &&
                <Tooltip title="Application actions">
                  <ActionsButton size="small" onClick={onClick}>
                    {isMobile ? <MoreHorizIcon /> : <ArrowDropDown />}
                  </ActionsButton>
                </Tooltip>}
            </SubheaderContainer>
          }
        />
        <StyledCardContent isLoading={status === 'loading'}>
          {status === 'loading' ? <Preloader /> :
            deviceGroups.map((group, i) => (
              <Fragment key={`${group.label} ${i}`}>
                {counter < MAX_APPLIANCE_DEVICES &&
                  <GroupLabel color="textSecondary">
                    {group.label || ''}
                  </GroupLabel>}
                <div>
                  {group.devices.map((d, index) => {
                    counter += 1;
                    const noAccess = !d.projects?.[0];
                    return (
                      counter < MAX_APPLIANCE_DEVICES + 1 ?
                        (d.id ?
                          <DeviceInfoLink
                            key={`${d.id} ${index}`}
                            to={noAccess ? '#' : `/device/${d.projects?.[0]}/${d.id}`}
                            isDisabled={noAccess}
                          >
                            {d.lastMetric?.youngest.temperature?._ ?
                              <DeviceInfo
                                warehouse
                                device={d}
                                lastTimestamp={d.lastMetric?.timestamp}
                                account={account}
                                loading={status === 'loading'}
                                alertTooltip={getAlertTooltip(d, group, appliance, account)}
                                devicesWithExcursions={devicesWithExcursions}
                              /> :
                              <NoDataWrapper>
                                <NoDataTitle>
                                  <span>{d.title}</span>
                                </NoDataTitle>
                                <NoDataContent>
                                  <ColoredSpan isRed>{noAccess ? 'No access' : 'No data'}</ColoredSpan>
                                  <StyledTooltip
                                    placement="right"
                                    title={getAlertTooltip(d, group, appliance, account)}
                                  >
                                    {d.projectAlertId ?
                                      <NotificationsActiveRoundedIcon /> :
                                      <NotificationsOffOutlinedIcon />}
                                  </StyledTooltip>
                                </NoDataContent>
                              </NoDataWrapper>}
                          </DeviceInfoLink> :
                          <NoMeasurementsContainer key={i}>
                            No measurements
                          </NoMeasurementsContainer>) :
                        null);
                  })}
                </div>
              </Fragment>))}
          {status !== 'loading' && counter > MAX_APPLIANCE_DEVICES &&
            <DeviceInfoLink to={`/warehouse/appliance-overview/${appliance?.id}`}>
              <Button fullWidth>{`Review (${counter})`}</Button>
            </DeviceInfoLink>}
        </StyledCardContent>
      </StyledCard>
    </CardWrapper>
  );
};

export default ApplianceCard;
